import { useQuery } from '@tanstack/react-query';
import {
    GridCallbackDetails,
    GridColDef,
    GridPaginationModel,
    GridRowParams,
    GridSortModel,
} from '@mui/x-data-grid';

import {
    DataGrid,
    DataGridCard,
    dataGridNestedObjectValueGetter,
} from '@barboxx/admin-components';
import {
    ACTIVE_WIDTH,
    DISPLAY_ORDER_WIDTH,
    NAME_MIN_WIDTH,
    PRICE_TIER_WIDTH,
    renderPriceEditCell,
    useBusinessSelector,
} from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { BusinessProductPriceTier, PourType, PriceTier } from '@barboxx/models';

export type Columns = GridColDef<BusinessProductPriceTier.DetailsResponse>[];
const COLUMNS: Columns = [
    {
        field: 'product.brand.name',
        headerName: 'Brand Name',
        minWidth: NAME_MIN_WIDTH,
        flex: 1,
        valueGetter: dataGridNestedObjectValueGetter,
    },
    {
        field: 'product.name',
        headerName: 'Product Name',
        minWidth: NAME_MIN_WIDTH,
        flex: 1,
        valueGetter: dataGridNestedObjectValueGetter,
    },
    {
        field: 'priceAdjustment',
        headerName: 'Price Adjustment',
        width: 150,
        align: 'right',
        headerAlign: 'right',
        renderEditCell: renderPriceEditCell,
    },
    {
        field: 'isActive',
        headerName: 'Active',
        width: ACTIVE_WIDTH,
        type: 'boolean',
        align: 'center',
        headerAlign: 'center',
    },
    {
        field: 'isDefault',
        headerName: 'Default',
        width: ACTIVE_WIDTH,
        type: 'boolean',
        align: 'center',
        headerAlign: 'center',
    },
    {
        field: 'displayOrder',
        headerName: 'Display Order',
        width: DISPLAY_ORDER_WIDTH,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
    },
];

interface Props {
    data: BusinessProductPriceTier[];
    size: number;
    page: number;
    total: number;
    onRowClick: (id: string) => void;
    onChangeSort: (sort: GridSortModel) => void;
    handleChangePageModel: (
        model: GridPaginationModel,
        details: GridCallbackDetails
    ) => void;
}

export function BusinessProductPriceTierTable(props: Props) {
    const {
        data = [],
        total,
        size,
        page,
        onRowClick,
        handleChangePageModel,
        onChangeSort,
    } = props;

    const { businessList } = useBusinessSelector();

    const priceTiersQuery = useQuery({
        queryKey: ['priceTiers'],
        queryFn: () => Api.Core.PriceTier.getPriceTiers(),
    });

    const pourTypesQuery = useQuery({
        queryKey: ['pourTypes'],
        queryFn: () => Api.Servings.PourType.getPourTypes(),
    });

    const handleRowClick = (param: GridRowParams) => {
        onRowClick(param.id as string);
    };

    const columns: Columns = [
        {
            field: 'businessId',
            headerName: 'Business',
            width: NAME_MIN_WIDTH,
            type: 'singleSelect',
            valueOptions(params) {
                return (
                    businessList.map(business => ({
                        value: business.id,
                        label: business.place.name,
                    })) ?? []
                );
            },
        },
        ...COLUMNS.slice(0, 2),
        {
            field: 'priceTierId',
            headerName: 'Price Tier',
            width: PRICE_TIER_WIDTH,
            type: 'singleSelect',
            valueOptions(params) {
                return (
                    priceTiersQuery.data?.data?.results.map(
                        (priceTier: PriceTier) => ({
                            value: priceTier.id,
                            label: priceTier.name,
                        })
                    ) ?? []
                );
            },
        },
        {
            field: 'pourTypeId',
            headerName: 'Pour Type',
            editable: true,
            width: PRICE_TIER_WIDTH,
            type: 'singleSelect',
            valueOptions(params) {
                return (
                    pourTypesQuery.data?.data?.results.map(
                        (pourType: PourType) => ({
                            value: pourType.id,
                            label: pourType.name,
                        })
                    ) ?? []
                );
            },
        },
        ...COLUMNS.slice(2),
    ];

    return (
        <DataGridCard title="Business Product Price Tiers">
            <DataGrid
                rows={data}
                columns={columns}
                paginationModel={{
                    page,
                    pageSize: size,
                }}
                onPaginationModelChange={handleChangePageModel}
                rowCount={total}
                disableColumnMenu
                onRowClick={handleRowClick}
                onSortModelChange={onChangeSort}
            />
        </DataGridCard>
    );
}
